import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { pipe, pathOr, includes, path, isNil } from 'ramda'
import { breakpoints } from '@/styles'
import { ValueText } from '@/features/Application/elements'
import { InformationTable } from '@/features/Application/elements/tables'
import useMediaQuery from '@/hooks/use-media-query'
import CellComponent from './cell-component'
import CreditSummaryBar from './credit-summary-bar'

const CreditSummaryView = ({ data, isLocked, isLoading }) => {
  const { t } = useTranslation()
  const isMobile = useMediaQuery(`(max-width: ${breakpoints.tabletLandscape})`)

  const text = {
    remaining_balance: t(
      'l.application.view.total_remaining_balance.label',
      'Total Balance Remaining:'
    ),
    monthly_payments: t(
      'l.application.view.total_monthly_payments.label',
      'Total Monthly Payments:'
    ),
    credit_limit: t('l.application.view.total_credit_limit.label', 'Total Credit Limit:'),
    late_payments: t('l.application.view.existing_late_payments.label', 'Existing Late Payments:'),
    average_days_paid: t('l.application.view.average_days_to_pay.label', 'Average Days to Pay:'),
    rate: t('l.application.view.rating.label', 'Rating:')
  }
  const columns = useMemo(
    () => [
      {
        id: 'header row',
        Header: () => null,
        Cell: ({ row: { original } }) => {
          return <ValueText type="small">{text?.[original]}</ValueText>
        }
      },
      {
        Header: t('l.application.view.revolving.label', 'Revolving'),
        Cell: ({ row: { original } }) => {
          const value = path(['revolving', original], data)
          const rate = pathOr('', ['revolving', 'rate'], data)
          const hasWarning = pipe(pathOr([], ['revolving', 'warnings']), includes(original))(data)
          const hasData = original === 'credit_limit' ? !!value : !isNil(value)
          return (
            <CellComponent
              original={original}
              value={value}
              rate={rate}
              hasWarning={hasWarning}
              hasData={hasData}
              isLocked={isLocked}
            />
          )
        }
      },
      {
        Header: t('l.application.view.instalment.label', 'Instalment'),
        Cell: ({ row: { original } }) => {
          const value = pathOr('', ['installment', original], data)
          const rate = pathOr('', ['installment', 'rate'], data)
          const hasWarning = pipe(pathOr([], ['installment', 'warnings']), includes(original))(data)
          const hasData = original === 'credit_limit' ? !!value : !isNil(value)
          return (
            <CellComponent
              original={original}
              value={value}
              rate={rate}
              hasWarning={hasWarning}
              hasData={hasData}
              isLocked={isLocked}
            />
          )
        }
      },
      {
        Header: t('l.application.view.mortgage.label', 'Mortgage'),
        Cell: ({ row: { original } }) => {
          const value = pathOr('', ['mortgage', original], data)
          const rate = pathOr('', ['mortgage', 'rate'], data)
          const hasWarning = pipe(pathOr([], ['mortgage', 'warnings']), includes(original))(data)
          const hasData = original === 'credit_limit' ? !!value : !isNil(value)
          return (
            <CellComponent
              original={original}
              value={value}
              rate={rate}
              hasWarning={hasWarning}
              hasData={hasData}
              isLocked={isLocked}
            />
          )
        }
      }
    ],
    [isLocked, isLoading]
  )

  const rowHeaders = [
    'remaining_balance',
    'monthly_payments',
    'credit_limit',
    'late_payments',
    'average_days_paid'
  ]

  return (
    <>
      <CreditSummaryBar data={data} isLoading={isLoading} isLocked={isLocked} />
      <InformationTable
        data={rowHeaders}
        columns={columns}
        hideHeader={isMobile}
        isLoading={isLoading}
      />
    </>
  )
}

export default CreditSummaryView
